import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import GlobalRiskDashboard from "./GlobalRiskDashboard";
import seaTurtleSvg from "../assets/sea-turtle.svg";
import { LocationInput } from "./astrology/LocationInput";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { getSecureHeaders } from "../services";
import { auth } from "../firebaseConfig";
import { onAuthStateChanged } from "firebase/auth";
import { useLocation } from "react-router-dom";
import { useLocalizedPath } from "../utils/navigation";
import {
  buildAdIntentSearchParams,
  clearPendingAdMode,
  getAdModeFromSearchParams,
  storePendingAdMode,
} from "../utils/adLandingIntent";

type LocationState = {
  locationName: string;
  latitude: string;
  longitude: string;
  timezoneOffset: string;
  country?: string;
};

type RiskResponse = {
  raw?: any;
  fusion?: { text?: string } | null;
  server_date_utc?: string;
  location_key?: string;
} | any;

type QaMessage = { role: "assistant" | "user"; text: string };

const DEFAULT_DELHI: LocationState = {
  locationName: "New Delhi, India",
  latitude: "28.613900",
  longitude: "77.209000",
  timezoneOffset: "5.5",
  country: "IN",
};

const WORLD_CAPITALS: { name: string; countryName: string; countryCode: string; lat: string; lon: string; tz: string }[] = [
  { name: "New Delhi", countryName: "India", countryCode: "IN", lat: "28.6139", lon: "77.2090", tz: "5.5" },
  { name: "Washington DC", countryName: "United States", countryCode: "US", lat: "38.8951", lon: "-77.0364", tz: "-5" },
  { name: "Beijing", countryName: "China", countryCode: "CN", lat: "39.9042", lon: "116.4074", tz: "8" },
  { name: "Moscow", countryName: "Russia", countryCode: "RU", lat: "55.7558", lon: "37.6173", tz: "3" },
  { name: "London", countryName: "United Kingdom", countryCode: "GB", lat: "51.5074", lon: "-0.1278", tz: "0" },
  { name: "Tel Aviv", countryName: "Israel", countryCode: "IL", lat: "32.0853", lon: "34.7818", tz: "2" },
  { name: "Tehran", countryName: "Iran", countryCode: "IR", lat: "35.6892", lon: "51.3890", tz: "3.5" },
  { name: "Tokyo", countryName: "Japan", countryCode: "JP", lat: "35.6762", lon: "139.6503", tz: "9" },
  { name: "Kyiv", countryName: "Ukraine", countryCode: "UA", lat: "50.4501", lon: "30.5234", tz: "2" },
  { name: "Taipei", countryName: "Taiwan", countryCode: "TW", lat: "25.0330", lon: "121.5654", tz: "8" },
  { name: "Seoul", countryName: "South Korea", countryCode: "KR", lat: "37.5665", lon: "126.9780", tz: "9" },
  { name: "Islamabad", countryName: "Pakistan", countryCode: "PK", lat: "33.6844", lon: "73.0479", tz: "5" },
  { name: "Riyadh", countryName: "Saudi Arabia", countryCode: "SA", lat: "24.7136", lon: "46.6753", tz: "3" },
  { name: "Berlin", countryName: "Germany", countryCode: "DE", lat: "52.5200", lon: "13.4050", tz: "1" },
  { name: "Paris", countryName: "France", countryCode: "FR", lat: "48.8566", lon: "2.3522", tz: "1" },
  { name: "Brasilia", countryName: "Brazil", countryCode: "BR", lat: "-15.7975", lon: "-47.8919", tz: "-3" },
];

function toIsoDate(value: Date) {
  return value.toISOString().slice(0, 10);
}

const ONBOARDING_DISMISSED_KEY = "globalRisk_onboarding_dismissed";

const QA_STATUS_MESSAGES = [
  "Analyzing risk factors...",
  "Cross-referencing planetary transits...",
  "Evaluating directional influences...",
  "Consulting Koorma Chakra framework...",
  "Assessing regional vulnerabilities...",
  "Synthesizing Vedic indicators...",
  "Compiling risk assessment...",
];

function QaStatusRotator() {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setIdx(p => (p + 1) % QA_STATUS_MESSAGES.length), 3000);
    return () => clearInterval(t);
  }, []);
  return (
    <p className="mt-2 text-xs text-slate-400 dark:text-slate-500 animate-pulse" key={idx}>
      {QA_STATUS_MESSAGES[idx]}
    </p>
  );
}

// Arrow icon for suggestion buttons (matches ChatPage)
const ArrowUpIcon = ({ className }: { className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
    <path strokeLinecap="round" strokeLinejoin="round" d="M5 10l7-7m0 0l7 7m-7-7v18" />
  </svg>
);

export default function GlobalRiskPage() {
  const { t, i18n } = useTranslation("chat");
  const API_BASE_URL = import.meta.env.VITE_APP_SERVER;
  const locationState = useLocation();
  const localizedPath = useLocalizedPath();
  const requestedMode = useMemo(
    () => getAdModeFromSearchParams(new URLSearchParams(locationState.search)),
    [locationState.search],
  );
  const [location, setLocation] = useState<LocationState>(DEFAULT_DELHI);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<any | null>(null);
  const [fusionText, setFusionText] = useState<string>("");
  const [fetchError, setFetchError] = useState<string>("");

  const [question, setQuestion] = useState("");
  const [qaLoading, setQaLoading] = useState(false);
  const [messages, setMessages] = useState<QaMessage[]>([]);
  const [askError, setAskError] = useState<string>("");

  const [showOnboarding, setShowOnboarding] = useState(() => {
    return localStorage.getItem(ONBOARDING_DISMISSED_KEY) !== "true";
  });
  const [showMethodology, setShowMethodology] = useState(false);
  const [reportReadyMessage, setReportReadyMessage] = useState("");
  const [showNonIndiaContextDetails, setShowNonIndiaContextDetails] = useState(false);
  const [authResolved, setAuthResolved] = useState(Boolean(auth.currentUser));
  const [isAuthenticated, setIsAuthenticated] = useState(Boolean(auth.currentUser?.emailVerified));

  const pageTopRef = useRef<HTMLDivElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  const isMissingAuthError = fetchError.includes("Authorization header missing");

  const canCall = useMemo(() => {
    const latOk = !!location.latitude && !Number.isNaN(Number(location.latitude));
    const lonOk = !!location.longitude && !Number.isNaN(Number(location.longitude));
    const tzOk = !!location.timezoneOffset && !Number.isNaN(Number(location.timezoneOffset));
    return !!location.locationName && latOk && lonOk && tzOk;
  }, [location]);

  const showNonIndiaReferenceNotice = useMemo(() => {
    const countryCode = String(location.country || "").trim().toUpperCase();
    const locationName = String(location.locationName || "");
    const countryName = String((location as any).countryName || "");
    const isIndia = countryCode === "IN" || /(^|,\s*)india$/i.test(locationName) || /\bindia\b/i.test(countryName);
    return !!(countryCode || locationName) && !isIndia;
  }, [location]);

  const suggestedQuestions = useMemo(() => [
    t("chat.global_risk.sq_1"),
    t("chat.global_risk.sq_2"),
    t("chat.global_risk.sq_3"),
    t("chat.global_risk.sq_4"),
    t("chat.global_risk.sq_5"),
    t("chat.global_risk.sq_6"),
  ], [t]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setIsAuthenticated(Boolean(user && user.emailVerified));
      setAuthResolved(true);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (requestedMode !== 'global-risk' || !authResolved) return;

    if (!isAuthenticated) {
      storePendingAdMode('global-risk');
      const redirectParams = buildAdIntentSearchParams(locationState.search);
      const redirectSearch = redirectParams.toString();
      window.location.href = redirectSearch
        ? `${localizedPath('/sign-up')}?${redirectSearch}`
        : localizedPath('/sign-up');
      return;
    }

    clearPendingAdMode();
    try {
      const cleanUrl = new URL(window.location.href);
      cleanUrl.searchParams.delete('mode');
      window.history.replaceState({}, document.title, cleanUrl.toString());
    } catch {}
  }, [authResolved, isAuthenticated, localizedPath, locationState.search, requestedMode]);

  const buildPayload = () => {
    const now = new Date();
    const start = toIsoDate(now);
    const end = toIsoDate(new Date(now.getTime() + 60 * 24 * 60 * 60 * 1000));
    return {
      location: {
        place_name: location.locationName,
        latitude: Number(location.latitude),
        longitude: Number(location.longitude),
        timezone_offset: Number(location.timezoneOffset),
      },
      start_date: start,
      end_date: end,
      high_threshold: 60.0,
      include_daily: true,
      include_fusion: true,
    };
  };

  const fetchRisk = async (options?: { announceCompletion?: boolean; scrollToTopOnSuccess?: boolean }) => {
    if (!canCall) return;
    setLoading(true);
    setFetchError("");
    setReportReadyMessage("");
    try {
      const headers = await getSecureHeaders();
      const res = await fetch(`${API_BASE_URL}/api/astrology/global-risk-radar`, {
        method: "POST",
        headers,
        body: JSON.stringify(buildPayload()),
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || `Failed (${res.status})`);
      }
      const payload: RiskResponse = await res.json();
      if (payload && typeof payload === "object" && ("raw" in payload || "fusion" in payload)) {
        setData((payload as any).raw ?? null);
        setFusionText(((payload as any).fusion?.text as string) || "");
      } else {
        setData(payload);
        setFusionText("");
      }
      if (options?.announceCompletion) {
        setReportReadyMessage(t("chat.global_risk.report_generated", { defaultValue: "Risk assessment ready" }));
      }
      if (options?.scrollToTopOnSuccess) {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    } catch (e: any) {
      setFetchError(e?.message || t("chat.global_risk.fetch_error"));
    } finally {
      setLoading(false);
    }
  };

  // Auto-fetch on mount (Delhi default)
  useEffect(() => {
    if (!authResolved) return;
    if (requestedMode === 'global-risk' && !isAuthenticated) return;
    void fetchRisk();
    setMessages([]);
    setQuestion("");
  }, [authResolved, isAuthenticated, requestedMode]);

  const prevCoordsRef = useRef(`${location.latitude}|${location.longitude}|${location.timezoneOffset}`);
  useEffect(() => {
    const nextKey = `${location.latitude}|${location.longitude}|${location.timezoneOffset}`;
    if (prevCoordsRef.current === nextKey) return;
    prevCoordsRef.current = nextKey;
    if (!canCall) return;
    void fetchRisk();
    setMessages([]);
    setQuestion("");
  }, [location.latitude, location.longitude, location.timezoneOffset, canCall]);

  // Animate suggestion buttons on mount
  useEffect(() => {
    if (!suggestionsRef.current) return;
    const buttons = suggestionsRef.current.querySelectorAll('.risk-suggestion');
    buttons.forEach((button, index) => {
      if (button) {
        (button as HTMLElement).style.opacity = '0';
        (button as HTMLElement).style.animation = 'none';
        void (button as HTMLElement).offsetWidth;
        (button as HTMLElement).style.animation = `fadeInUp 0.4s ease forwards ${0.1 + index * 0.05}s`;
      }
    });
  }, []);

  const ask = async (q?: string) => {
    if (!canCall) return;
    const question_text = (q || question).trim();
    if (!question_text) return;
    setQaLoading(true);
    setAskError("");
    setMessages((prev) => [...prev, { role: "user", text: question_text }]);
    if (!q) setQuestion("");
    try {
      const headers = await getSecureHeaders();
      const locationPayload = {
        place_name: location.locationName,
        latitude: Number(location.latitude),
        longitude: Number(location.longitude),
        timezone_offset: Number(location.timezoneOffset),
      };
      const res = await fetch(`${API_BASE_URL}/chat`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          user_prompt: question_text,
          history: [],
          authority: [0, 10],
          doc_types: [],
          mode: "normal",
          is_fast_search: false,
          listening_mode: false,
          locale: i18n.language,
          personal_mode: false,
          global_risk_mode: true,
          global_risk_location: locationPayload,
        }),
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || `Failed (${res.status})`);
      }
      const payload = await res.json();
      const answer = typeof payload?.answer === "string" ? payload.answer : JSON.stringify(payload);
      setMessages((prev) => [...prev, { role: "assistant", text: answer }]);
    } catch (e: any) {
      setAskError(e?.message || t("chat.global_risk.ask_error"));
      setMessages((prev) => [...prev, { role: "assistant", text: e?.message || t("chat.global_risk.ask_error") }]);
    } finally {
      setQaLoading(false);
    }
  };

  const handleCapitalClick = (cap: typeof WORLD_CAPITALS[number]) => {
    setLocation({
      locationName: `${cap.name}, ${cap.countryName}`,
      latitude: cap.lat,
      longitude: cap.lon,
      timezoneOffset: cap.tz,
      country: cap.countryCode,
    });
  };

  const dismissOnboarding = () => {
    setShowOnboarding(false);
    localStorage.setItem(ONBOARDING_DISMISSED_KEY, "true");
  };

  const locationControls = (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-950/40">
      <div className="text-sm font-semibold text-slate-900 dark:text-slate-100">{t("chat.global_risk.select_location")}</div>

      <div className="mt-3">
        <div className="mb-2 text-xs text-slate-500 dark:text-slate-400">{t("chat.global_risk.quick_capitals")}</div>
        <div className="flex flex-wrap gap-1.5" role="group" aria-label={t("chat.global_risk.quick_capitals")}>
          {WORLD_CAPITALS.map((cap) => (
            <button
              key={cap.name}
              onClick={() => handleCapitalClick(cap)}
              className={`capital-pill ${location.locationName === cap.name || location.locationName === `${cap.name}, ${cap.countryName}` ? 'active' : ''}`}
              disabled={loading}
              aria-pressed={location.locationName === cap.name || location.locationName === `${cap.name}, ${cap.countryName}`}
            >
              {cap.name}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-4">
        <LocationInput
          data={location}
          onChange={(next) => setLocation(next)}
          label={t("chat.global_risk.city_country")}
          showInlineHelp={true}
        />
      </div>

      <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
        <Button onClick={() => fetchRisk({ announceCompletion: true, scrollToTopOnSuccess: true })} disabled={!canCall || loading} aria-busy={loading}>
          {loading ? t("chat.global_risk.assessing_btn") : t("chat.global_risk.assess_btn")}
        </Button>
        <div className="text-xs text-slate-500 dark:text-slate-400">
          {t("chat.global_risk.cached_note")}
        </div>
      </div>
    </div>
  );

  return (
    <div ref={pageTopRef} className="mx-auto w-full max-w-6xl px-4 pb-10">
      <style>{`
        .risk-suggestion {
          display: flex;
          align-items: center;
          padding: 10px 12px;
          background: rgba(255, 255, 255, 0.6);
          border: 1px solid rgba(226, 232, 240, 0.9);
          border-radius: 10px;
          cursor: pointer;
          transition: all 0.25s ease;
          color: #475569;
          font-size: 13px;
          font-weight: 400;
          text-align: left;
          width: 100%;
          position: relative;
          overflow: hidden;
        }
        .risk-suggestion:disabled {
          cursor: not-allowed;
          opacity: 0.6;
        }
        .dark .risk-suggestion {
          background: rgba(51, 65, 85, 0.5);
          border-color: rgba(71, 85, 105, 0.8);
          color: #cbd5e1;
        }
        .risk-suggestion:hover {
          background: #ffffff;
          border-color: rgba(249, 115, 22, 0.4);
          transform: translateY(-1px) scale(1.01);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
          color: #1e293b;
        }
        .dark .risk-suggestion:hover {
          background: rgba(71, 85, 105, 0.9);
          color: #ffffff;
        }
        .risk-suggestion::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(249, 115, 22, 0.08), transparent);
          transition: left 0.6s ease;
        }
        .risk-suggestion:hover::before {
          left: 100%;
        }
        .risk-suggestion:hover .suggestion-arrow {
          color: #f97316;
          transform: translateX(2px);
        }
        .risk-suggestion:hover .suggestion-text {
          font-weight: 500;
        }
        .suggestion-arrow {
          margin-right: 10px;
          color: #94a3b8;
          transition: color 0.25s ease, transform 0.25s ease;
          flex-shrink: 0;
        }
        .suggestion-text {
          transition: font-weight 0.25s ease;
        }
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(15px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .capital-pill {
          padding: 5px 12px;
          border-radius: 9999px;
          font-size: 12px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
          border: 1px solid rgba(226, 232, 240, 0.8);
          background: rgba(255, 255, 255, 0.7);
          color: #475569;
          white-space: nowrap;
        }
        .capital-pill:hover {
          background: #f97316;
          color: white;
          border-color: #f97316;
          transform: translateY(-1px);
          box-shadow: 0 2px 8px rgba(249, 115, 22, 0.25);
        }
        .capital-pill.active {
          background: #ea580c;
          color: white;
          border-color: #ea580c;
        }
        .dark .capital-pill {
          background: rgba(51, 65, 85, 0.6);
          border-color: rgba(71, 85, 105, 0.8);
          color: #cbd5e1;
        }
        .dark .capital-pill:hover {
          background: #f97316;
          color: white;
          border-color: #f97316;
        }
        .dark .capital-pill.active {
          background: #ea580c;
          color: white;
          border-color: #ea580c;
        }
      `}</style>

      {/* Onboarding banner */}
      {showOnboarding && (
        <div className="mt-4 rounded-xl border border-orange-200 bg-gradient-to-r from-orange-50 to-amber-50 px-4 py-3 dark:border-orange-500/20 dark:from-orange-950/30 dark:to-amber-950/20" role="alert">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-3">
              <div className="mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg bg-white p-1" aria-hidden="true">
                <img src={seaTurtleSvg} alt="" className="h-full w-full" />
              </div>
              <p className="text-sm text-slate-700 dark:text-slate-200">
                {t("chat.global_risk.onboarding_text")}
              </p>
            </div>
            <button
              onClick={dismissOnboarding}
              className="flex-shrink-0 rounded-md px-3 py-1 text-xs font-medium text-orange-700 hover:bg-orange-100 dark:text-orange-300 dark:hover:bg-orange-900/30"
              aria-label={t("chat.global_risk.onboarding_dismiss")}
            >
              {t("chat.global_risk.onboarding_dismiss")}
            </button>
          </div>
        </div>
      )}

      {showNonIndiaReferenceNotice && (
        <div className="mt-4 rounded-xl border border-sky-200 bg-gradient-to-r from-sky-50 to-indigo-50 px-4 py-3 dark:border-sky-500/20 dark:from-sky-950/20 dark:to-indigo-950/20" role="note" aria-live="polite">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-sm font-medium text-sky-900 dark:text-sky-200">
                Non-India location selected: directional sectors still follow the Bharata-centered Koorma frame.
              </p>
              <p className="mt-1 text-xs text-sky-800 dark:text-sky-300">
                Example: if you select Tehran, the chart timing and eclipse visibility are calculated for Tehran, but “North-East” still refers to the Bharata-centered sector, not north-east of Tehran.
              </p>
              {showNonIndiaContextDetails && (
                <div className="mt-2 space-y-1 text-xs text-sky-800 dark:text-sky-300">
                  <p>Best way to read this mode: it is a global Bharata-centered Koorma/Nama directional model whose timing and observational context are adjusted to the selected place.</p>
                  <p>Delhi → India-centered frame + Delhi-calculated transits/visibility.</p>
                  <p>Tehran → India-centered frame + Tehran-calculated transits/visibility.</p>
                  <p>Tokyo → India-centered frame + Tokyo-calculated transits/visibility.</p>
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={() => setShowNonIndiaContextDetails((prev) => !prev)}
              className="flex-shrink-0 rounded-md px-3 py-1 text-xs font-medium text-sky-700 hover:bg-sky-100 dark:text-sky-300 dark:hover:bg-sky-900/30"
            >
              {showNonIndiaContextDetails ? "Read less" : "Read more"}
            </button>
          </div>
        </div>
      )}

      <div className="mt-6">
        {locationControls}
      </div>

      {/* Dashboard */}
      <div className="mt-6">
        <GlobalRiskDashboard data={data || {}} title={t("chat.global_risk.dashboard_title")} loading={loading} />
      </div>

      {reportReadyMessage && !loading && !fetchError && (
        <div className="mt-4 rounded-xl border border-emerald-300 bg-emerald-50 px-4 py-3 shadow-sm dark:border-emerald-500/30 dark:bg-emerald-950/20" role="status" aria-live="polite">
          <p className="text-sm font-medium text-emerald-800 dark:text-emerald-300">
            {reportReadyMessage}
          </p>
        </div>
      )}

      {/* Fetch error state */}
      {fetchError && (
        <div className="mt-4 rounded-xl border border-red-200 bg-red-50 p-4 dark:border-red-500/20 dark:bg-red-950/20" role="alert" aria-live="assertive">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0">
              <p className="text-sm text-red-700 dark:text-red-300">{fetchError}</p>
              {isMissingAuthError && (
                <p className="mt-1 text-xs text-red-600 dark:text-red-400">
                  Session may need to reload. Please refresh the page, then try again.
                </p>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-2 sm:ml-3">
              {isMissingAuthError && (
                <Button variant="outline" size="sm" onClick={() => window.location.reload()} className="text-xs">
                  Refresh page
                </Button>
              )}
              <Button variant="outline" size="sm" onClick={fetchRisk} className="text-xs">
                {t("chat.global_risk.retry")}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Fusion text report */}
      <div id="global-risk-report" className="mt-6 rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-950/40">
        <div className="text-sm font-semibold text-slate-900 dark:text-slate-100">{t("chat.global_risk.report_title")}</div>
        <div className="mt-2 prose prose-sm dark:prose-invert max-w-none text-slate-700 dark:text-slate-200">
          {fusionText ? (
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{fusionText}</ReactMarkdown>
          ) : (
            <p>{loading ? t("chat.global_risk.report_loading") : t("chat.global_risk.report_placeholder")}</p>
          )}
        </div>
      </div>

      {/* Methodology explainer */}
      <div className="mt-4">
        <button
          onClick={() => setShowMethodology(!showMethodology)}
          className="flex items-center gap-2 text-xs text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 transition-colors"
          aria-expanded={showMethodology}
        >
          <svg className={`w-3 h-3 transition-transform ${showMethodology ? 'rotate-90' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
          </svg>
          {t("chat.global_risk.methodology_title")}
        </button>
        {showMethodology && (
          <div className="mt-2 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-xs text-slate-600 dark:border-slate-700 dark:bg-slate-900/40 dark:text-slate-300">
            {t("chat.global_risk.methodology_text")}
          </div>
        )}
      </div>

      <div className="mt-6">
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-950/40">
          <div className="text-sm font-semibold text-slate-900 dark:text-slate-100">{t("chat.global_risk.ask_title")}</div>

          {/* Suggested questions */}
          {messages.length === 0 && (
            <div className="mt-3" ref={suggestionsRef}>
              <div className="text-xs text-slate-500 dark:text-slate-400 mb-2">{t("chat.global_risk.suggested_title")}</div>
              <div className="space-y-1.5">
                {suggestedQuestions.map((sq, idx) => (
                  <button
                    key={idx}
                    onClick={() => ask(sq)}
                    className="risk-suggestion"
                    disabled={qaLoading}
                  >
                    <ArrowUpIcon className="suggestion-arrow w-3.5 h-3.5" />
                    <span className="suggestion-text">{sq}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="mt-3">
            <Textarea
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder={t("chat.global_risk.ask_placeholder")}
              className="min-h-[80px]"
              aria-label={t("chat.global_risk.ask_title")}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  ask();
                }
              }}
            />
          </div>
          <div className="mt-3 flex items-center gap-3">
            <Button onClick={() => ask()} disabled={!canCall || qaLoading || !question.trim()} aria-busy={qaLoading}>
              {qaLoading ? t("chat.global_risk.answering_btn") : t("chat.global_risk.ask_btn")}
            </Button>
          </div>
          {qaLoading && <QaStatusRotator />}

          {/* Ask error state */}
          {askError && (
            <div className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700 dark:border-red-500/20 dark:bg-red-950/20 dark:text-red-300" role="alert">
              {askError}
            </div>
          )}

          {/* Messages */}
          <div className="mt-4 space-y-3">
            {messages.map((m, idx) => (
              <div
                key={idx}
                className={`rounded-lg border px-3 py-2 text-sm ${
                  m.role === "user"
                    ? "border-slate-200 bg-slate-50 text-slate-900 dark:border-slate-800 dark:bg-slate-900/40 dark:text-slate-100"
                    : "border-yellow-500/15 bg-yellow-500/5 text-slate-800 dark:bg-yellow-500/10 dark:text-slate-100"
                }`}
              >
                <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  {m.role === "user" ? t("chat.global_risk.you_label") : t("chat.global_risk.sarvalok_label")}
                </div>
                <div className={`mt-1 ${m.role === "user" ? "whitespace-pre-wrap" : "prose prose-sm dark:prose-invert max-w-none"}`}>
                  {m.role === "user" ? m.text : <ReactMarkdown remarkPlugins={[remarkGfm]}>{m.text}</ReactMarkdown>}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
